import React, { useState, useEffect, useMemo, useRef } from "react";
import { useAppSelector } from "../../store/hooks";
import { selectIsAdmin } from "../../store/authSelectors";
import { useParams } from "react-router-dom";
import { TabPanel } from "primereact/tabview";
import UserInstanceRolesSection from "../../components/Access/UserInstanceRolesSection";
import { ConfirmDialog } from "../../components/Dialogs/AccessDialogs";
import { PanelTabs } from "../../components/Access/Access.styles";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { useNavigate } from "react-router-dom";
import { License, EnrichedLicense } from "../../store/types";
import { useUsersAPI } from "../../hooks/api/use-usersAPI";
import { useCompaniesAPI } from "../../hooks/api/use-companiesAPI";
import { useProjectsAPI } from "../../hooks/api/use-projectsAPI";
import {
  useLicensesAPI,
  buildLicensesEndpoint,
  fromLegacyUserLicense,
} from "../../hooks/api/use-licensesAPI";
import { useGetQuery } from "../../store/slices/apiSlice";
import { formatPhone } from "../../hooks/validation";
import { isSessionLicense } from "../../hooks/useLicenseType";
import {
  enrichLicense,
  getLicenseStats,
  licenseSearchText,
  matchesLicenseStat,
} from "../../hooks/licenseHelpers";
import type { LicenseStatFilter } from "../../hooks/licenseHelpers";
import {
  Container,
  ContainerXScroll,
} from "../../components/Container/Container";
import CustomInputText from "../../components/CustomInput/CustomInputText";
import CustomDatatable from "../../components/CustomDatatable/CustomDatatable";
import type { DatatableHandle } from "../../components/CustomDatatable/CustomDatatable";
import StatsPanel from "../../components/StatsPanel/StatsPanel";
import type { StatItem } from "../../components/StatsPanel/StatsPanel";
import {
  userLicenseColumns,
  getLicenseContextMenuItems,
  getLicenseMobileActions,
  sessionLicenseRowExpansionTemplate,
} from "../../components/DatatableTemplates/LicenseTableTemplates";
import AddLicenseDialogButton from "../../components/DialogButton/AddLicenseDialogButton";
import UserDialogs from "../../components/Dialogs/UserDialogs";
import SendLinkDialogs from "../../components/Dialogs/SendLinkDialogs";
import LicenseDialogs from "../../components/Dialogs/LicenseDialogs";
import StatusTag from "../../components/StatusTag/StatusTag";
import { H2 } from "../../styles/Typography";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import { StyledIconLine } from "../../components/StyledIcon/StyledIcon";
import {
  IconUserFilled,
  IconCompanyFilled,
  IconMailFilled,
  IconPhoneFilled,
  IconDocFilled,
  IconDocCheckFilled,
  IconDocWarnFilled,
  IconDocErrorFilled,
  IconArrowLeft,
  IconSearch,
  IconEdit,
  IconDelete,
  IconSend,
  IconLock,
} from "../../assets/icons/Icon";
import {
  ContainerFlex,
  ContainerColumnFlex,
  CardsColumnContainer,
  FlexColumn,
  ButtonContainer,
  ButtonLabel,
  SearchContainer,
  NotActivatedFlex,
  SendLinkButton,
} from "./UserDetails.styles";
import {
  FlexLineJustify,
  FlexLineGapSmall,
  FilterComponents,
  PageName,
  Label,
  ButtonBox,
} from "../../styles/PageLayoutStyles.styles";
import { Spinner } from "../CRUDPages/CRUDPages.styles";

const UserDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 768px)") || false;
  const isAdmin = useAppSelector(selectIsAdmin);
  const me = useAppSelector((state) => state.auth.user);

  const tableRef = useRef<DatatableHandle<EnrichedLicense>>(null);
  const [searchValue, setSearchValue] = useState<string>("");
  const [licenseList, setLiscenseList] = useState<EnrichedLicense[]>([]);
  const [totalLicenses, setTotalLicenses] = useState(0);
  const [activeLicenses, setActiveLicenses] = useState(0);
  const [expiringLicenses, setExpiringLicenses] = useState(0);
  const [inactiveLicenses, setInactiveLicenses] = useState(0);

  /** Карточка статистики, по которой отфильтрована таблица лицензий */
  const [statFilter, setStatFilter] = useState<LicenseStatFilter>("all");

  const [selectedLicense, setSelectedLicense] = useState<License | null>(null);
  const [userDialogVisible, setUserDialogVisible] = useState<boolean>(false);
  const [userDialogType, setUserDialogType] = useState<string>("");
  const [licenseDialogVisible, setLicenseDialogVisible] =
    useState<boolean>(false);
  const [licenseDialogType, setLicenseDialogType] = useState<string>("");
  const [linkDialogVisible, setLinkDialogVisible] = useState<boolean>(false);
  const [linkDialogType, setLinkDialogType] = useState<string>("");
  const [blockDialogVisible, setBlockDialogVisible] = useState<boolean>(false);

  const {
    usersEP,
    meEP,
    users,
    handleUpdateUser,
    handleDeleteUser,
    handleSendActivationLink,
    handleSendResetPasswordLink,
    handleSetUserBlocked,
    toastRef: userToastRef,
    refetchData,
  } = useUsersAPI();
  const { companiesEP, companiesDropdownOpts } = useCompaniesAPI();
  const { projects, projectsDropdownOpts } = useProjectsAPI();
  const {
    licensesEP,
    handlePostLicense,
    handleActivateLicense,
    handleDeactivateLicense,
    handleUpdateLicense,
    handleAssignSeat,
    handleRemoveSeat,
    handleDeleteLicense,
    toastRef,
  } = useLicensesAPI(undefined, { skipList: true });

  const userEndpoint = isAdmin ? `${usersEP}/${id}` : meEP;
  // админ: GET /api/v2/licenses?userId={id};
  // «мои лицензии»: аналога в v2 нет, поэтому остаётся v1 /users/me/licenses
  const licensesEndpoint = isAdmin
    ? buildLicensesEndpoint(licensesEP, { userId: id })
    : `${meEP}/licenses`;

  const {
    data: user,
    refetch: refetchUser,
    isLoading,
  } = useGetQuery(userEndpoint, {
    refetchOnMountOrArgChange: true,
    skip: !id || !usersEP || !meEP,
  });

  const { data: licenses, refetch: refetchLicenses } = useGetQuery(
    licensesEndpoint,
    {
      refetchOnMountOrArgChange: true,
      skip: !id || !usersEP || !meEP,
    }
  );

  const { data: company, refetch: refetchCompany } = useGetQuery(
    `${companiesEP}/${user?.organizationId}`,
    {
      refetchOnMountOrArgChange: true,
      skip: !id || !companiesEP || !user || !isAdmin,
    }
  );

  useEffect(() => {
    if (licenses) {
      // не-админ получает лицензии из v1 /users/me/licenses — приводим их к модели v2
      const normalized: License[] = isAdmin
        ? licenses
        : licenses.map(fromLegacyUserLicense);

      setLiscenseList(
        normalized.map((license) =>
          enrichLicense(license, {
            projects,
            organizationName: company?.name ?? user?.organizationName,
          })
        )
      );

      const stats = getLicenseStats(normalized);
      setTotalLicenses(stats.total);
      setActiveLicenses(stats.active);
      setExpiringLicenses(stats.expiring);
      setInactiveLicenses(stats.inactive);
    } else {
      setLiscenseList([]);
      setTotalLicenses(0);
      setActiveLicenses(0);
      setExpiringLicenses(0);
      setInactiveLicenses(0);
    }
  }, [licenses, company, projects, user, isAdmin]);

  const filteredData = useMemo(() => {
    if (!searchValue.trim() || !licenseList) return licenseList;
    const lowerSearch = searchValue.toLowerCase();
    return licenseList.filter((license) =>
      licenseSearchText(license).includes(lowerSearch)
    );
  }, [licenseList, searchValue]);

  const statItems: StatItem[] = useMemo(
    () => [
      {
        id: "all",
        icon: <IconDocFilled size={24} />,
        header: totalLicenses.toString(),
        title: "Количество лицензий",
      },
      {
        id: "active",
        icon: <IconDocCheckFilled size={24} />,
        header: activeLicenses.toString(),
        title: "Активные лицензии",
      },
      {
        id: "expiring",
        icon: <IconDocWarnFilled size={24} />,
        header: expiringLicenses.toString(),
        title: "Истекающие лицензии",
      },
      {
        id: "inactive",
        icon: <IconDocErrorFilled size={24} />,
        header: inactiveLicenses.toString(),
        title: "Неактивные лицензии",
      },
    ],
    [totalLicenses, activeLicenses, expiringLicenses, inactiveLicenses]
  );

  /** Связь панели статистики с таблицей лицензий */
  const applyStatFilter = (filter: LicenseStatFilter) => {
    setStatFilter(filter);
    tableRef.current?.setRowFilter(
      "stat",
      filter === "all"
        ? null
        : (license) => matchesLicenseStat(license, filter)
    );
  };

  const handleStatSelect = (id: string) => {
    const filter = id as LicenseStatFilter;
    // повторный клик по той же карточке снимает фильтр
    applyStatFilter(statFilter === filter ? "all" : filter);
  };

  const handleEnableLicense = (license: License) => {
    setSelectedLicense(license);

    if (isMobile) {
      navigate(`/licenses/form?mode=enable&id=${license.id}`);
    } else {
      setLicenseDialogType("enable");
      setLicenseDialogVisible(true);
    }
  };

  const handleDisableLicense = (license: License) => {
    setSelectedLicense(license);

    if (isMobile) {
      navigate(`/licenses/form?mode=disable&id=${license.id}`);
    } else {
      setLicenseDialogType("disable");
      setLicenseDialogVisible(true);
    }
  };

  const handleAddUser = (license: License) => {
    setSelectedLicense(license);

    if (isMobile) {
      navigate(`/licenses/form?mode=addUser&id=${license.id}`);
    } else {
      setLicenseDialogType("addUser");
      setLicenseDialogVisible(true);
    }
  };

  const editLicenseUser = (license: License) => {
    setSelectedLicense(license);

    if (isMobile) {
      navigate(`/licenses/form?mode=editUser&id=${license.id}`);
    } else {
      setLicenseDialogType("editUser");
      setLicenseDialogVisible(true);
    }
  };

  const deleteLicenseUser = (license: License) => {
    setSelectedLicense(license);

    if (isMobile) {
      navigate(`/licenses/form?mode=deleteUser&id=${license.id}`);
    } else {
      setLicenseDialogType("deleteUser");
      setLicenseDialogVisible(true);
    }
  };

  const contextMenuItems = useMemo(
    () =>
      getLicenseContextMenuItems(
        selectedLicense,
        handleAddUser,
        editLicenseUser,
        deleteLicenseUser,
        handleEnableLicense,
        handleDisableLicense
      ),
    [selectedLicense, handleAddUser, handleEnableLicense, handleDisableLicense]
  );

  const mobileActions = useMemo(
    () => getLicenseMobileActions(handleEnableLicense, handleDisableLicense),
    [handleEnableLicense, handleDisableLicense]
  );

  const handleView = (license: License) => {
    navigate(`/licenses/${license.id}`);
  };

  const handleSelectionChange = (selected: License | null) => {
    setSelectedLicense(selected);
  };

  const resetAllFilters = () => {
    tableRef.current?.reset();
    setSearchValue("");
    setStatFilter("all");
  };

  const editLicense = (license: License) => {
    setSelectedLicense(license);

    if (isMobile) {
      navigate(`/licenses/form?mode=edit&id=${license.id}`);
    } else {
      setLicenseDialogType("edit");
      setLicenseDialogVisible(true);
    }
  };

  const deleteLicense = (license: License) => {
    setSelectedLicense(license);

    if (isMobile) {
      navigate(`/licenses/form?mode=delete&id=${license.id}`);
    } else {
      setLicenseDialogType("delete");
      setLicenseDialogVisible(true);
    }
  };

  const editUser = () => {
    if (isMobile) {
      navigate(`/users/form?mode=edit&id=${user.id}`);
    } else {
      setUserDialogType("edit");
      setUserDialogVisible(true);
    }
  };

  const deleteUser = () => {
    if (isMobile) {
      navigate(`/users/form?mode=delete&id=${user.id}`);
    } else {
      setUserDialogType("delete");
      setUserDialogVisible(true);
    }
  };

  const deleteUserAPI = async (): Promise<boolean> => {
    try {
      let success = await handleDeleteUser(user);
      if (success) {
        return true;
      }
      return false;
    } catch (error) {
      return false;
    } finally {
      setTimeout(() => {
        navigate(-1);
      }, 2000);
      refetchData();
    }
  };

  const openLinkDialog = (type: string) => {
    setLinkDialogType(type);
    setLinkDialogVisible(true);
  };

  const setBlocked = async (blocked: boolean): Promise<boolean> => {
    const success = await handleSetUserBlocked(user.id, blocked);
    if (success) refetchUser();
    return success;
  };

  if (isLoading)
    return (
      <Container>
        <Spinner>
          <ProgressSpinner />
          <p>Загрузка...</p>
        </Spinner>
      </Container>
    );

  return (
    <ContainerFlex>
      <Toast ref={toastRef} position="bottom-center" />
      <Toast ref={userToastRef} position="bottom-center" />
      <ContainerColumnFlex>
        <CardsColumnContainer>
          <Container>
            <FlexLineJustify>
              <H2>Данные пользователя</H2>
              {isAdmin && (
                <ButtonBox>
                  <Button text onClick={editUser}>
                    <IconEdit size={16} />
                  </Button>
                  <Button text onClick={deleteUser}>
                    <IconDelete size={16} />
                  </Button>
                </ButtonBox>
              )}
            </FlexLineJustify>
            <FlexColumn>
              <StyledIconLine
                icon={<IconUserFilled size={20} />}
                text={
                  user
                    ? `${user.lastName ?? ""} ${user.name ?? ""} ${
                        user.patronymic ?? ""
                      }`
                    : ""
                }
              />
              <StyledIconLine
                icon={<IconCompanyFilled size={20} />}
                text={user?.organizationName}
              />
              <StyledIconLine
                icon={<IconMailFilled size={20} />}
                text={user?.email}
              />
              <StyledIconLine
                icon={<IconPhoneFilled size={20} />}
                text={user.phone ? formatPhone(user!.phone) : "Нет данных"}
              />
              {isAdmin &&
                user &&
                (!user?.isActivated ? (
                  <NotActivatedFlex>
                    <StatusTag status="danger" label="Не активирован" />
                    <SendLinkButton
                      onClick={() => openLinkDialog("activation")}
                    >
                      <IconSend size={16} />
                      Отправить ссылку активации
                    </SendLinkButton>
                  </NotActivatedFlex>
                ) : (
                  <NotActivatedFlex>
                    <SendLinkButton
                      onClick={() => openLinkDialog("resetPassword")}
                    >
                      <IconSend size={16} />
                      Отправить ссылку сброса пароля
                    </SendLinkButton>
                  </NotActivatedFlex>
                ))}
              {isAdmin && user && (
                <NotActivatedFlex>
                  {!user.isEnabled && (
                    <StatusTag status="danger" label="Заблокирован" />
                  )}
                  {user.isEnabled ? (
                    me?.id !== user.id && (
                      <SendLinkButton onClick={() => setBlockDialogVisible(true)}>
                        <IconLock size={16} />
                        Заблокировать
                      </SendLinkButton>
                    )
                  ) : (
                    <SendLinkButton onClick={() => setBlocked(false)}>
                      <IconLock size={16} />
                      Разблокировать
                    </SendLinkButton>
                  )}
                </NotActivatedFlex>
              )}
            </FlexColumn>
          </Container>

          <StatsPanel
            items={statItems}
            selectedId={statFilter === "all" ? null : statFilter}
            onSelect={handleStatSelect}
          />
        </CardsColumnContainer>

        {isAdmin && (
          <ButtonContainer>
            <Button outlined onClick={() => navigate(-1)}>
              <IconArrowLeft size={16} />
              <ButtonLabel>Назад</ButtonLabel>
            </Button>
          </ButtonContainer>
        )}
      </ContainerColumnFlex>

      <ContainerXScroll>
        {/* все вкладки смонтированы: карточки статистики слева фильтруют таблицу лицензий через ref */}
        <PanelTabs renderActiveOnly={false}>
        <TabPanel header="Лицензии">
        <FlexLineJustify>
          <PageName />
          <FlexLineGapSmall>
            {isAdmin && (
              <AddLicenseDialogButton
                user={user}
                company={company}
                projectsOptions={projectsDropdownOpts}
                handleActivateLicense={handleActivateLicense}
                handleDeactivateLicense={handleDeactivateLicense}
                handleAddLicense={handlePostLicense}
                handleEditLicense={handleUpdateLicense}
                handleAssignSeat={handleAssignSeat}
                handleDeleteLicense={handleDeleteLicense}
                refetchData={refetchLicenses}
                licensesEP={licensesEP}
                isMobile={isMobile}
              />
            )}
            <FilterComponents>
              <Button
                icon="pi pi-filter-slash"
                onClick={resetAllFilters}
                outlined
              >
                <Label>Сбросить</Label>
              </Button>
              <SearchContainer>
                <CustomInputText
                  value={searchValue}
                  setValue={setSearchValue}
                  placeholder="Поиск..."
                  icon={<IconSearch size={20} />}
                />
              </SearchContainer>
            </FilterComponents>
          </FlexLineGapSmall>
        </FlexLineJustify>

        <CustomDatatable<License>
          ref={tableRef}
          data={filteredData ?? []}
          columns={userLicenseColumns}
          onView={handleView}
          onEdit={editLicense}
          onDelete={deleteLicense}
          onRowDoubleClick={isAdmin ? handleView : undefined}
          selected={selectedLicense}
          onSelectionChange={handleSelectionChange}
          getRowId={(license) => license.id}
          extraContextMenuItems={contextMenuItems}
          hideViewButton={!isAdmin}
          mobileActions={mobileActions}
          hideContextMenu={!isAdmin}
          sortable
          rowExpansionTemplate={
            isAdmin ? undefined : sessionLicenseRowExpansionTemplate
          }
          allowExpansion={
            !isAdmin ? (license) => isSessionLicense(license.kind) : false
          }
        />
        <LicenseDialogs
          visible={licenseDialogVisible}
          onHide={() => setLicenseDialogVisible(false)}
          dialogType={licenseDialogType}
          user={user}
          usersList={users}
          license={selectedLicense!}
          enrichedLicense={selectedLicense as EnrichedLicense}
          handleActivateLicense={handleActivateLicense}
          handleDeactivateLicense={handleDeactivateLicense}
          handleAddLicense={handlePostLicense}
          handleEditLicense={handleUpdateLicense}
          handleAssignSeat={handleAssignSeat}
          handleRemoveSeat={handleRemoveSeat}
          handleDeleteLicense={handleDeleteLicense}
          refetchData={refetchLicenses}
          companiesOptions={companiesDropdownOpts}
          projectsOptions={projectsDropdownOpts}
          licensesEP={licensesEP}
        />
        </TabPanel>
        {isAdmin && (
          <TabPanel header="Роли в инстансах">
            {id && <UserInstanceRolesSection userId={id} />}
          </TabPanel>
        )}
        </PanelTabs>
      </ContainerXScroll>

      <ConfirmDialog
        visible={blockDialogVisible}
        header="Заблокировать пользователя"
        text="Вход будет запрещён, все сессии пользователя в ComradeMajor и приложениях завершатся, аренды лицензий освободятся."
        confirmLabel="Заблокировать"
        onHide={() => setBlockDialogVisible(false)}
        onConfirm={() => setBlocked(true)}
      />

      <UserDialogs
        visible={userDialogVisible}
        onHide={() => setUserDialogVisible(false)}
        dialogType={userDialogType}
        user={user}
        handleEditUser={handleUpdateUser}
        handleDeleteUser={deleteUserAPI}
        refetchData={refetchUser}
        companiesOptions={companiesDropdownOpts}
      />
      <SendLinkDialogs
        visible={linkDialogVisible}
        onHide={() => setLinkDialogVisible(false)}
        dialogType={linkDialogType}
        user={user}
        sendLink={
          linkDialogType === "activation"
            ? handleSendActivationLink
            : handleSendResetPasswordLink
        }
      />
    </ContainerFlex>
  );
};

export default UserDetails;
